import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock } from "lucide-react";
import { HorarioPuntos, HorarioCarnet, HorarioLeyenda } from "@/components/HorarioCurso";
import schedule from "@/data/schedule.generated.json";
import { API } from "@/lib/faro";

const MES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

// "2026-09-18" -> "18 de septiembre de 2026"
function fechaHumanaES(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} de ${MES[m - 1]} de ${y}`;
}

// Formatea el rango start-end del schedule generado. Si start === end, sólo
// muestra un día. Si difieren, "18 al 21 de septiembre de 2026".
function fechaRangoHumana(startDay, endDay) {
  if (!endDay || startDay === endDay) return fechaHumanaES(startDay);
  const [ys, ms] = startDay.split("-").map(Number);
  const [ye, me, de] = endDay.split("-").map(Number);
  const dsDay = Number(startDay.split("-")[2]);
  if (ys === ye && ms === me) {
    return `${dsDay} al ${de} de ${MES[ms - 1]} de ${ys}`;
  }
  return `${dsDay} de ${MES[ms - 1]} al ${de} de ${MES[me - 1]} de ${ye}`;
}

function fechaPresencialHumana(startDay, endDay) {
  const [ys, ms, ds] = startDay.split("-").map(Number);
  const [ye, me, de] = endDay.split("-").map(Number);
  const fin = me === ms ? String(de) : String(de) + " de " + MES[me - 1];
  return String(ds) + " de " + MES[ms - 1] + " PRESENCIAL y hasta el día " + fin + " para hacer 3 horas online";
}

export default function CourseScheduleMedia({ variant, hideChart = false, formatoPresencial = false }) {
  const tipo = variant === "carnet" ? "carnet" : "puntos";
  const code = tipo === "carnet" ? "B1" : "A";
  // Semilla del prerender: dato de build. Luego se reemplaza en cliente por
  // el schedule vivo (auto-borrado al día siguiente del inicio).
  const seedList = (schedule.convocatorias[code] || []).map((c) => ({
    startDay: c.startDay, endDay: c.endDay,
  }));
  const [live, setLive] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/courses/schedule`)
      .then((r) => { if (alive) setLive(r.data?.[code] || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [code]);
  const today = (() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
  })();
  const source = live !== null
    ? live.map((s) => ({ startDay: s.start, endDay: s.end }))
    : seedList;
  const proximas = source.filter((c) => c.startDay >= today).slice(0, 3);

  return (
    <section className="mt-8" data-testid={`schedule-media-${variant}`}>
      {!hideChart && (
        <>
          <h2 className="font-display font-black text-2xl sm:text-3xl">Horario del curso</h2>
          <p className="mt-3 text-[var(--faro-text-soft)]">
            Así se distribuyen las sesiones del curso.
          </p>
          <div data-testid={`schedule-horario-${variant}`}>
            {variant === "carnet" ? <HorarioCarnet /> : <HorarioPuntos />}
          </div>
          <HorarioLeyenda />
        </>
      )}
      {proximas.length > 0 && (
        <div className={hideChart ? "" : "mt-6"} data-testid={`schedule-next-dates-${variant}`}>
          {hideChart ? (
            <h2 className="font-display font-black flex items-center gap-2 text-2xl sm:text-3xl">
              <CalendarClock size={20} className="text-[var(--faro-red)]" /> Próximas fechas
            </h2>
          ) : (
            <h3 className="font-display font-black flex items-center gap-2 text-xl">
              <CalendarClock size={20} className="text-[var(--faro-red)]" /> Próximas fechas
            </h3>
          )}
          <ul className="mt-3 space-y-2">
            {proximas.map((c) => (
              <li
                key={c.startDay}
                className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] px-5 py-3 flex items-center gap-3"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: tipo === "carnet" ? "var(--faro-red)" : "var(--faro-blue)" }}
                  aria-hidden="true"
                ></span>
                <strong className="text-[var(--faro-blue-deep)]">
                  {formatoPresencial ? fechaPresencialHumana(c.startDay, c.endDay) : fechaRangoHumana(c.startDay, c.endDay)}
                </strong>
              </li>
            ))}
          </ul>
          <Link
            to="/proximos-cursos"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[var(--faro-blue)] hover:text-[var(--faro-red)] transition-colors"
            data-testid={`schedule-all-dates-link-${variant}`}
          >
            Ver el calendario completo de convocatorias <ArrowRight size={15} />
          </Link>
        </div>
      )}
    </section>
  );
}
